import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Link2, Eye, EyeOff, X, Check, Copy, RefreshCw, AlertTriangle } from 'lucide-react'
import { useBot, useUpdateBotIntegration } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { botsApi } from '@/shared/api/bots'

export default function BotIntegration() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: bot, isLoading } = useBot(botId)
  const updateIntegration = useUpdateBotIntegration()

  const [flyerKey, setFlyerKey] = useState<string>('')
  const [subgramKey, setSubgramKey] = useState<string>('')
  const [tgrassKey, setTgrassKey] = useState<string>('')
  const [gramadsKey, setGramadsKey] = useState<string>('')
  const [showFlyerKey, setShowFlyerKey] = useState(false)
  const [showSubgramKey, setShowSubgramKey] = useState(false)
  const [showTgrassKey, setShowTgrassKey] = useState(false)
  const [showGramadsKey, setShowGramadsKey] = useState(false)
  const [showBotKey, setShowBotKey] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showReissueModal, setShowReissueModal] = useState(false)
  const [copiedBotKey, setCopiedBotKey] = useState(false)
  const [error, setError] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasFlyerKeyInDB, setHasFlyerKeyInDB] = useState(false)
  const [hasSubgramKeyInDB, setHasSubgramKeyInDB] = useState(false)
  const [hasTgrassKeyInDB, setHasTgrassKeyInDB] = useState(false)
  const [hasGramadsKeyInDB, setHasGramadsKeyInDB] = useState(false)

  const queryClient = useQueryClient()

  // Store handleSave ref to avoid recreating MainButton
  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize keys from bot data only once
  useEffect(() => {
    if (bot && !isInitialized) {
      const flyerKeyFromDB = bot.flyer_key || ''
      const subgramKeyFromDB = bot.subgram_key || ''
      const tgrassKeyFromDB = bot.tgrass_key || ''
      const gramadsKeyFromDB = bot.gramads_key || ''

      setFlyerKey(flyerKeyFromDB)
      setSubgramKey(subgramKeyFromDB)
      setTgrassKey(tgrassKeyFromDB)
      setGramadsKey(gramadsKeyFromDB)
      setHasFlyerKeyInDB(!!flyerKeyFromDB)
      setHasSubgramKeyInDB(!!subgramKeyFromDB)
      setHasTgrassKeyInDB(!!tgrassKeyFromDB)
      setHasGramadsKeyInDB(!!gramadsKeyFromDB)
      setIsInitialized(true)
    }
  }, [bot, isInitialized])

  // Reset success state after 2 seconds
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess])

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copiedBotKey) {
      const timer = setTimeout(() => {
        setCopiedBotKey(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [copiedBotKey])

  // Mutation for regenerating bot key
  const regenerateBotKeyMutation = useMutation({
    mutationFn: () => botsApi.regenerateBotKey(botId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot', botId] })
      hapticNotification('success')
      setShowReissueModal(false)
    },
    onError: (error) => {
      console.error('Failed to regenerate bot key:', error)
      setError('Ошибка при перевыпуске ключа')
      hapticNotification('error')
    }
  })

  const handleCopyBotKey = async () => {
    if (!bot?.bot_key) return

    try {
      await navigator.clipboard.writeText(bot.bot_key)
      setCopiedBotKey(true)
      hapticFeedback('light')
    } catch (error) {
      console.error('Failed to copy bot key:', error)
      setError('Ошибка при копировании ключа')
    }
  }

  const handleReissueBotKey = () => {
    hapticFeedback('medium')
    regenerateBotKeyMutation.mutate()
  }

  const handleSave = async () => {
    if (!bot) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateIntegration.mutateAsync({
        botId: bot.id,
        data: {
          flyer_key: flyerKey,
          subgram_key: subgramKey,
          tgrass_key: tgrassKey,
          gramads_key: gramadsKey,
        },
      })
      hapticNotification('success')
      setShowSuccess(true)

      // Reset loading state and hide main button before navigation
      setMainButtonLoading(false)
      hideMainButton()
      document.body.style.backgroundColor = ''

      // Scroll to top before navigation
      window.scrollTo(0, 0)

      // Navigate back to bot details page with success message
      navigate(`/bots/${botId}`, {
        replace: true,
        state: { successMessage: 'Бот успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update integration:', error)
      setError('Ошибка при сохранении интеграций')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  // Update ref with latest handleSave
  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  // Setup MainButton and page background
  useEffect(() => {
    if (!isReady) return

    // Scroll to top on mount
    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    // Reset loading state and show button
    setMainButtonLoading(false)
    showMainButton('Сохранить', onSave, '#5288c1', '#ffffff')

    // Set page background color like bottom nav
    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  if (isLoading || !bot) {
    return <div className="min-h-screen bg-tg-bg flex items-center justify-center">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
              <Link2 className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Интеграция</h1>
              <p className="text-sm text-tg-hint mt-0.5">@{bot.username}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* OpService API Key - Primary Key Section */}
        <div className="border-2 border-indigo-500/30 rounded-xl bg-gradient-to-r from-indigo-500/5 to-purple-500/5 overflow-hidden">
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-bold text-tg-text">Ваш API ключ OpService</h2>
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs font-medium rounded-full">
                    Основной
                  </span>
                </div>
                <p className="text-xs text-tg-hint">
                  Используйте для подключения к платформе OpService
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="relative">
                <input
                  type={showBotKey ? 'text' : 'password'}
                  value={bot.bot_key || ''}
                  readOnly
                  className="w-full px-4 py-3 pr-32 text-base border-2 border-indigo-500/20 rounded-lg bg-tg-secondary-bg text-tg-text placeholder:text-tg-hint cursor-default focus:outline-none"
                  placeholder="Ключ не найден"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={handleCopyBotKey}
                    className="p-1.5 hover:bg-indigo-500/10 rounded-lg transition-colors"
                    title="Копировать ключ"
                  >
                    <Copy className={`w-4 h-4 ${copiedBotKey ? 'text-green-500' : 'text-indigo-400'}`} />
                  </button>
                  <button
                    onClick={() => {
                      setShowBotKey(!showBotKey)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-indigo-500/10 rounded-lg transition-colors"
                    title="Показать/скрыть ключ"
                  >
                    {showBotKey ? (
                      <EyeOff className="w-4 h-4 text-indigo-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-indigo-400" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowReissueModal(true)
                      hapticFeedback('medium')
                    }}
                    className="p-1.5 hover:bg-orange-500/10 rounded-lg transition-colors"
                    title="Перевыпустить ключ"
                  >
                    <RefreshCw className="w-4 h-4 text-orange-500" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {bot.bot_key && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Активен</span>
                    </div>
                  )}
                  {copiedBotKey && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Check className="w-3 h-3" />
                      <span>Скопировано</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* External Integrations Header */}
        <div className="pt-2">
          <h3 className="text-sm font-semibold text-tg-hint uppercase tracking-wide px-1">
            Внешние интеграции
          </h3>
        </div>

        {/* Flyer Integration */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img
                  src="/images/integrations/flyer.jpg"
                  alt="Flyer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Flyer</h2>
                <p className="text-xs text-tg-hint">Интеграция с Flyer</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showFlyerKey ? 'text' : 'password'}
                  value={flyerKey}
                  onChange={(e) => {
                    setFlyerKey(e.target.value)
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  className="w-full px-4 py-2.5 pr-20 text-base border-2 border-transparent rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-tg-bg text-tg-text placeholder:text-tg-hint"
                  placeholder="Введите ключ Flyer"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {flyerKey && (
                    <button
                      onClick={() => {
                        setFlyerKey('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowFlyerKey(!showFlyerKey)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                  >
                    {showFlyerKey ? (
                      <EyeOff className="w-4 h-4 text-tg-hint" />
                    ) : (
                      <Eye className="w-4 h-4 text-tg-hint" />
                    )}
                  </button>
                </div>
              </div>
              {hasFlyerKeyInDB && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
                  <Check className="w-3 h-3" />
                  <span>Ключ настроен</span>
                </div>
              )}
            </div>
            <p className="text-xs text-tg-hint">
              Получите API ключ в настройках вашего аккаунта Flyer
            </p>
          </div>
        </div>

        {/* Subgram Integration */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img
                  src="/images/integrations/subgram.jpg"
                  alt="Subgram"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Subgram</h2>
                <p className="text-xs text-tg-hint">Интеграция с Subgram</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showSubgramKey ? 'text' : 'password'}
                  value={subgramKey}
                  onChange={(e) => {
                    setSubgramKey(e.target.value)
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  className="w-full px-4 py-2.5 pr-20 text-base border-2 border-transparent rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-tg-bg text-tg-text placeholder:text-tg-hint"
                  placeholder="Введите ключ Subgram"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {subgramKey && (
                    <button
                      onClick={() => {
                        setSubgramKey('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowSubgramKey(!showSubgramKey)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                  >
                    {showSubgramKey ? (
                      <EyeOff className="w-4 h-4 text-tg-hint" />
                    ) : (
                      <Eye className="w-4 h-4 text-tg-hint" />
                    )}
                  </button>
                </div>
              </div>
              {hasSubgramKeyInDB && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
                  <Check className="w-3 h-3" />
                  <span>Ключ настроен</span>
                </div>
              )}
            </div>
            <p className="text-xs text-tg-hint">
              Получите API ключ в настройках вашего аккаунта Subgram
            </p>
          </div>
        </div>

        {/* Tgrass Integration */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img
                  src="/images/integrations/tgrass.svg"
                  alt="Tgrass"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Tgrass</h2>
                <p className="text-xs text-tg-hint">Интеграция с Tgrass</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showTgrassKey ? 'text' : 'password'}
                  value={tgrassKey}
                  onChange={(e) => {
                    setTgrassKey(e.target.value)
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  className="w-full px-4 py-2.5 pr-20 text-base border-2 border-transparent rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-tg-bg text-tg-text placeholder:text-tg-hint"
                  placeholder="Введите ключ Tgrass"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {tgrassKey && (
                    <button
                      onClick={() => {
                        setTgrassKey('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowTgrassKey(!showTgrassKey)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                  >
                    {showTgrassKey ? (
                      <EyeOff className="w-4 h-4 text-tg-hint" />
                    ) : (
                      <Eye className="w-4 h-4 text-tg-hint" />
                    )}
                  </button>
                </div>
              </div>
              {hasTgrassKeyInDB && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
                  <Check className="w-3 h-3" />
                  <span>Ключ настроен</span>
                </div>
              )}
            </div>
            <p className="text-xs text-tg-hint">
              Получите API ключ в @tgrassbot
            </p>
          </div>
        </div>

        {/* Gramads Integration */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img
                  src="/images/integrations/gramads.jpg"
                  alt="Gramads"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Gramads</h2>
                <p className="text-xs text-tg-hint">Интеграция с Gramads для обычных показов</p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-2">
                Bearer token
              </label>
              <div className="relative">
                <input
                  type={showGramadsKey ? 'text' : 'password'}
                  value={gramadsKey}
                  onChange={(e) => {
                    setGramadsKey(e.target.value)
                    setError('')
                  }}
                  onFocus={() => hapticFeedback('light')}
                  className="w-full px-4 py-2.5 pr-20 text-base border-2 border-transparent rounded-lg focus:border-indigo-500 focus:outline-none transition-colors bg-tg-bg text-tg-text placeholder:text-tg-hint"
                  placeholder="Введите токен Gramads"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {gramadsKey && (
                    <button
                      onClick={() => {
                        setGramadsKey('')
                        hapticFeedback('light')
                      }}
                      className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-tg-hint" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowGramadsKey(!showGramadsKey)
                      hapticFeedback('light')
                    }}
                    className="p-1.5 hover:bg-tg-hint/10 rounded-lg transition-colors"
                  >
                    {showGramadsKey ? (
                      <EyeOff className="w-4 h-4 text-tg-hint" />
                    ) : (
                      <Eye className="w-4 h-4 text-tg-hint" />
                    )}
                  </button>
                </div>
              </div>
              {hasGramadsKeyInDB && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
                  <Check className="w-3 h-3" />
                  <span>Ключ настроен</span>
                </div>
              )}
            </div>
            <p className="text-xs text-tg-hint">
              Используется в endpoint обычных показов при <code>is_gramads: true</code>
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-600">Интеграции успешно сохранены</p>
            </div>
          </div>
        )}
      </div>

      {/* Reissue Bot Key Confirmation Modal */}
      {showReissueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-tg-secondary-bg rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
              </div>
              <h2 className="text-xl font-bold text-tg-text">Перевыпустить API ключ?</h2>
            </div>

            <div className="space-y-3 mb-6">
              <p className="text-sm text-tg-text">
                Это действие немедленно сделает текущий API ключ недействительным.
              </p>
              <p className="text-sm text-tg-hint">
                Все интеграции, использующие старый ключ, перестанут работать. Вы уверены, что хотите продолжить?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReissueModal(false)
                  hapticFeedback('light')
                }}
                disabled={regenerateBotKeyMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-tg-bg text-tg-text rounded-lg font-medium hover:bg-tg-hint/10 transition-colors disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                onClick={handleReissueBotKey}
                disabled={regenerateBotKeyMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {regenerateBotKeyMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Перевыпуск...</span>
                  </>
                ) : (
                  'Перевыпустить'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
